export type PersistenceStatus = "unsaved" | "saving" | "saved" | "failed";

export interface SaveCoordinatorOptions<TPrepared> {
  debounceMs?: number;
  initialGeneration?: number;
  initiallyDirty?: boolean;
  prepare: (generation: number) => Promise<TPrepared>;
  persist: (prepared: TPrepared, generation: number) => Promise<void>;
  onStatusChange?: (status: PersistenceStatus) => void;
}

export const AUTOSAVE_DELAY_MS = 1_500;

export class SaveCoordinator<TPrepared> {
  private generation: number;
  private savedGeneration: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private activeSave: Promise<void> | null = null;
  private disposed = false;
  private status: PersistenceStatus;

  public constructor(private readonly options: SaveCoordinatorOptions<TPrepared>) {
    this.generation = options.initialGeneration ?? 0;
    this.savedGeneration = options.initiallyDirty ? this.generation - 1 : this.generation;
    this.status = options.initiallyDirty ? "unsaved" : "saved";
  }

  public markDirty(): number {
    if (this.disposed) return this.generation;
    this.generation += 1;
    this.setStatus("unsaved");
    if (!this.activeSave) this.schedule();
    return this.generation;
  }

  public getStatus(): PersistenceStatus {
    return this.status;
  }

  public isDirty(): boolean {
    return this.savedGeneration < this.generation;
  }

  public getGeneration(): number {
    return this.generation;
  }

  public flush(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    this.cancelTimer();
    if (this.activeSave) return this.activeSave;
    this.activeSave = this.runSaveLoop().finally(() => {
      this.activeSave = null;
    });
    return this.activeSave;
  }

  public retry(): Promise<void> {
    return this.flush();
  }

  public dispose(): void {
    this.disposed = true;
    this.cancelTimer();
  }

  private schedule(): void {
    this.cancelTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush().catch(() => undefined);
    }, this.options.debounceMs ?? AUTOSAVE_DELAY_MS);
  }

  private cancelTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private async runSaveLoop(): Promise<void> {
    try {
      while (this.savedGeneration < this.generation) {
        const targetGeneration = this.generation;
        this.setStatus("saving");
        const prepared = await this.options.prepare(targetGeneration);

        // Encoding is asynchronous. If the drawing changed while it ran, discard
        // that stale result before it can enter the database transaction.
        if (targetGeneration !== this.generation) continue;

        await this.options.persist(prepared, targetGeneration);
        this.savedGeneration = Math.max(this.savedGeneration, targetGeneration);
      }
      if (!this.isDirty()) this.setStatus("saved");
    } catch (error) {
      this.setStatus("failed");
      throw error;
    }
  }

  private setStatus(status: PersistenceStatus): void {
    this.status = status;
    if (!this.disposed) this.options.onStatusChange?.(status);
  }
}
